import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { api } from "@/lib/api-client"

// ── Types ────────────────────────────────────────────────────────

export interface SmsAccount {
  id: string
  provider: string
  name: string
  sender_name: string | null
  is_active: boolean
  settings: Record<string, unknown>
  created_at: string
}

export interface SmsMessage {
  id: string
  sms_account_id: string
  user_id: string | null
  to_number: string
  from_name: string | null
  body: string
  status: "pending" | "queued" | "sent" | "delivered" | "failed"
  external_id: string | null
  error_message: string | null
  parts_count: number
  cost: string | null
  sent_at: string | null
  delivered_at: string | null
  entity_type: string | null
  entity_id: string | null
  sms_template_id: string | null
  created_at: string
}

export interface SmsTemplate {
  id: string
  name: string
  body: string
  category: string | null
  is_shared: boolean
  user_id: string | null
  usage_count: number
  variables: string[]
  is_active: boolean
  created_at: string
}

// ── SMS Accounts ─────────────────────────────────────────────────

export function useSmsAccounts() {
  return useQuery({
    queryKey: ["sms_accounts"],
    queryFn: () => api.get<{ data: SmsAccount[] }>("/api/v1/sms_accounts"),
  })
}

export function useCreateSmsAccount() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<SmsAccount> & { api_token?: string }) =>
      api.post<{ data: SmsAccount }>("/api/v1/sms_accounts", data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sms_accounts"] }),
  })
}

export function useUpdateSmsAccount() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<SmsAccount> & { api_token?: string } }) =>
      api.patch<{ data: SmsAccount }>(`/api/v1/sms_accounts/${id}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sms_accounts"] }),
  })
}

export function useDeleteSmsAccount() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/v1/sms_accounts/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sms_accounts"] }),
  })
}

// ── SMS Messages ─────────────────────────────────────────────────

interface SmsMessageFilters {
  status?: string
  entity_type?: string
  entity_id?: string
}

export function useSmsMessages(filters: SmsMessageFilters = {}) {
  const params = new URLSearchParams()
  if (filters.status) params.set("status", filters.status)
  if (filters.entity_type) params.set("entity_type", filters.entity_type)
  if (filters.entity_id) params.set("entity_id", filters.entity_id)
  const q = params.toString()

  return useQuery({
    queryKey: ["sms_messages", filters],
    queryFn: () =>
      api.get<{ data: SmsMessage[] }>(`/api/v1/sms_messages${q ? `?${q}` : ""}`),
  })
}

export function useSendSms() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: {
      sms_account_id: string
      to_number: string
      body: string
      entity_type?: string
      entity_id?: string
      sms_template_id?: string
    }) => api.post<{ data: SmsMessage }>("/api/v1/sms_messages", data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sms_messages"] }),
  })
}

export function useBulkSendSms() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: {
      sms_account_id: string
      contact_ids: string[]
      template_id?: string
      body?: string
    }) => api.post("/api/v1/sms_messages/bulk", data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sms_messages"] }),
  })
}

// ── SMS Templates ────────────────────────────────────────────────

export function useSmsTemplates(filters: { category?: string } = {}) {
  const params = new URLSearchParams()
  if (filters.category) params.set("category", filters.category)
  const q = params.toString()

  return useQuery({
    queryKey: ["sms_templates", filters],
    queryFn: () =>
      api.get<{ data: SmsTemplate[] }>(`/api/v1/sms_templates${q ? `?${q}` : ""}`),
  })
}

export function useCreateSmsTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<SmsTemplate>) =>
      api.post<{ data: SmsTemplate }>("/api/v1/sms_templates", data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sms_templates"] }),
  })
}

export function useUpdateSmsTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<SmsTemplate> }) =>
      api.patch<{ data: SmsTemplate }>(`/api/v1/sms_templates/${id}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sms_templates"] }),
  })
}

export function useDeleteSmsTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/v1/sms_templates/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sms_templates"] }),
  })
}

export function useSmsTemplateUse() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.post(`/api/v1/sms_templates/${id}/use`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sms_templates"] }),
  })
}

export function useRenderSmsTemplate() {
  return useMutation({
    mutationFn: ({ id, variables }: { id: string; variables: Record<string, string> }) =>
      api.post<{ data: { body: string } }>(
        `/api/v1/sms_templates/${id}/render_template`,
        { variables }
      ),
  })
}
